import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { groupCartLinesBySeller } from '@redevicer/shared'
import { listingsAPI, paymentsAPI } from '../api/client'
import { useCart } from '../hooks/useCart'
import { blocksOwnListingAction } from '../utils/testDeals'
import { listingCardThumb } from '../utils/listingThumb'
import { IconCart } from './AppNavIcons'
import ListingNoPhotoPlaceholder from './ListingNoPhotoPlaceholder'

interface ListingImage {
  id: number
  thumbnail_url: string
  display_url?: string
  is_primary: boolean
}

interface Listing {
  id: number
  title: string
  price: number
  condition: string
  owner_id: number
  is_sold: boolean
  phone_model: { manufacturer: string; model_name: string; hero_image_path?: string | null; release_year?: number | null }
  images?: ListingImage[]
}

interface CartPageProps {
  token: string | null
  currentUserId?: number
  onOpenListing: (id: number) => void
  onRequireLogin?: () => void
  onBrowse?: () => void
}

export default function CartPage({
  token,
  currentUserId,
  onOpenListing,
  onRequireLogin,
  onBrowse,
}: CartPageProps) {
  const { t } = useTranslation()
  const { lines, remove, clear } = useCart()
  const [items, setItems] = useState<Listing[]>([])
  const [loading, setLoading] = useState(false)
  const [checkoutBusyOwner, setCheckoutBusyOwner] = useState<number | null>(null)
  const [loadError, setLoadError] = useState('')

  const idsKey = useMemo(() => lines.map((l) => l.listing_id).join(','), [lines])

  useEffect(() => {
    if (lines.length === 0) {
      setItems([])
      setLoadError('')
      setLoading(false)
      return
    }

    let cancelled = false
    const listingIds = lines.map((line) => line.listing_id)
    setLoading(true)
    setLoadError('')

    Promise.allSettled(
      listingIds.map((id) =>
        listingsAPI.getListing(id).then((r) => r.data as Listing),
      ),
    )
      .then((results) => {
        if (cancelled) return
        const rows: Listing[] = []
        const soldIds: number[] = []
        let failed = 0
        for (const r of results) {
          if (r.status === 'fulfilled') {
            rows.push(r.value)
            if (r.value.is_sold) soldIds.push(r.value.id)
          } else {
            failed += 1
          }
        }
        setItems(rows)
        if (failed > 0 && rows.length === 0) {
          setLoadError(t('cart.loadError'))
        }
        if (soldIds.length > 0) {
          queueMicrotask(() => {
            if (!cancelled) soldIds.forEach((id) => remove(id))
          })
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, remove])

  const sellerGroups = useMemo(() => {
    return groupCartLinesBySeller(lines).map((group) => {
      const groupListings = group.lines
        .map((line) => items.find((l) => l.id === line.listing_id))
        .filter(Boolean) as Listing[]
      const available = groupListings.filter(
        (l) => !l.is_sold && !blocksOwnListingAction(l.owner_id, currentUserId),
      )
      const subtotal = available.reduce((sum, l) => sum + l.price, 0)
      return { ...group, groupListings, available, subtotal }
    })
  }, [lines, items, currentUserId])

  const grandTotal = sellerGroups.reduce((sum, g) => sum + g.subtotal, 0)
  const multiSeller = sellerGroups.length > 1

  function thumbFor(listing: Listing): { src: string | null; isUserPhoto: boolean } {
    const th = listingCardThumb(listing)
    return { src: th.src, isUserPhoto: th.isUserPhoto }
  }

  function checkoutGroup(ownerId: number) {
    if (!token) {
      onRequireLogin?.()
      return
    }
    const group = sellerGroups.find((g) => g.owner_id === ownerId)
    if (!group || group.available.length === 0 || checkoutBusyOwner !== null) return

    setCheckoutBusyOwner(ownerId)
    paymentsAPI
      .cartCheckout(
        group.available.map((l) => l.id),
        token,
        { route: 'guarantee' },
      )
      .then((res) => {
        const cleared = (res.data?.cleared_listing_ids as number[] | undefined) ?? []
        for (const id of cleared) remove(id)
        const url = res.data?.checkout_url as string | undefined
        if (url) {
          window.location.href = url
          return
        }
        alert(res.data?.message || t('cart.checkoutReserved'))
      })
      .catch((err) => {
        alert(err.response?.data?.detail || err.message || t('cart.checkoutFailed'))
      })
      .finally(() => setCheckoutBusyOwner(null))
  }

  return (
    <section className="cart-page cart-page--modern">
      {lines.length > 0 && (
        <header className="cart-page__hero">
          <div className="cart-page__hero-icon" aria-hidden="true">
            <IconCart />
          </div>
          <h2>{t('cart.title')}</h2>
          <p className="cart-page__hint">{t('cart.hint')}</p>
        </header>
      )}

      {loadError && <p className="cart-page-error">{loadError}</p>}

      {!loading && lines.length === 0 && (
        <div className="cart-empty cart-empty--bloom">
          <div className="cart-empty__glow" aria-hidden="true" />
          <div className="cart-empty__icon" aria-hidden="true">
            <IconCart />
          </div>
          <p className="cart-empty__title">{t('cart.empty')}</p>
          <p className="cart-empty__hint">{t('cart.emptyHint')}</p>
          {onBrowse ? (
            <button type="button" className="cart-empty__cta" onClick={onBrowse}>
              {t('cart.browseCta')}
            </button>
          ) : null}
        </div>
      )}

      {lines.length > 0 && (
        <div className="cart-page__body">
          {sellerGroups.map((group, groupIdx) => (
            <section
              key={group.owner_id}
              className="cart-seller-group cart-seller-group--modern"
              style={{ animationDelay: `${0.08 + groupIdx * 0.06}s` }}
            >
              {multiSeller && (
                <h3 className="cart-seller-group__title">
                  {t('cart.sellerGroup', { id: group.owner_id })}
                </h3>
              )}
              <ul className="cart-lines">
                {group.lines.map((line) => {
                  const listing = items.find((l) => l.id === line.listing_id)
                  const thumbInfo = listing ? thumbFor(listing) : null
                  const thumb = thumbInfo?.src ?? null
                  const unavailable =
                    !listing ||
                    listing.is_sold ||
                    blocksOwnListingAction(listing.owner_id, currentUserId)

                  return (
                    <li key={line.listing_id} className="cart-line">
                      <button
                        type="button"
                        className="cart-line-main"
                        onClick={() => onOpenListing(line.listing_id)}
                      >
                        <div className="cart-line-thumb">
                          {thumb ? (
                            <img src={thumb} alt="" />
                          ) : (
                            <ListingNoPhotoPlaceholder variant="thumb" />
                          )}
                        </div>
                        <div className="cart-line-info">
                          {listing ? (
                            <>
                              <p className="cart-line-title">{listing.title}</p>
                              <p className="cart-line-meta">
                                {listing.phone_model.manufacturer} {listing.phone_model.model_name}
                              </p>
                              <p className="cart-line-price">€{listing.price.toFixed(2)}</p>
                            </>
                          ) : loading ? (
                            <p className="cart-line-title">{t('common.loading')}</p>
                          ) : (
                            <p className="cart-line-title">{t('cart.loadError')}</p>
                          )}
                          {unavailable && listing && (
                            <p className="cart-line-unavailable">{t('cart.itemUnavailable')}</p>
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        className="cart-line-remove"
                        onClick={() => remove(line.listing_id)}
                        aria-label={t('cart.remove')}
                      >
                        ×
                      </button>
                    </li>
                  )
                })}
              </ul>

              {multiSeller && (
                <div className="cart-seller-group__footer">
                  <p className="cart-seller-group__subtotal">
                    {t('cart.subtotal')}: <strong>€{group.subtotal.toFixed(2)}</strong>
                  </p>
                  <button
                    type="button"
                    className="listing-detail-buy-btn cart-checkout-btn cart-seller-group__checkout"
                    disabled={checkoutBusyOwner !== null || group.available.length === 0}
                    onClick={() => checkoutGroup(group.owner_id)}
                  >
                    {checkoutBusyOwner === group.owner_id
                      ? '…'
                      : token
                        ? t('cart.checkoutGroup')
                        : t('cart.checkoutLogin')}
                  </button>
                </div>
              )}
            </section>
          ))}

          <div className="cart-footer cart-footer--modern">
            <p className="cart-total">
              {t('cart.total')}: <strong>€{grandTotal.toFixed(2)}</strong>
            </p>
            <div className="cart-footer-actions">
              <button
                type="button"
                className="cart-clear-btn"
                onClick={clear}
                disabled={checkoutBusyOwner !== null}
              >
                {t('cart.clear')}
              </button>
              {!multiSeller && sellerGroups[0] && (
                <button
                  type="button"
                  className="listing-detail-buy-btn cart-checkout-btn"
                  disabled={
                    checkoutBusyOwner !== null || sellerGroups[0].available.length === 0
                  }
                  onClick={() => checkoutGroup(sellerGroups[0].owner_id)}
                >
                  {checkoutBusyOwner !== null
                    ? '…'
                    : token
                      ? t('cart.checkout')
                      : t('cart.checkoutLogin')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
